
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBackToDashboard } from "../../utils/navigationUtils";
import { exportLeadGeneratorApplications, exportDashboardStats } from "../../services/exportService";
import {
  LayoutDashboard,
  Building,
  Users,
  FileText,
  Settings,
  Shield,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Search,
  Filter,
  Download,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Star,
  Activity,
  BarChart,
  PieChart,
  BriefcaseBusinessIcon,
} from "lucide-react";
import {
  getHospitalApplications,
  approveHospitalApplication,
  rejectHospitalApplication,
  getApplicationStats,
  getLeadApplicationStats,
  getHospitalsByReferrer,
} from "../../services/hospitalService";
import { useAuth } from "../../context/AuthContext";
import { SignOutIconButton } from "../../component/common/SignOutButton";
import LeadGeneratorHospitals from "../lead/LeadManageHospitals";
import NotificationBell from "../../components/common/NotificationBell";
import LeadApplicationsList from "../lead/LeadApplicationsList";
import RoleSwitcher from "../../components/common/RoleSwitcher";

const LeadDashboard = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("dashboard");
  const [hospitalApplications, setHospitalApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "",
    searchTerm: "",
    dateFrom: "",
    dateTo: "",
    state: "",
  });
  const [stats, setStats] = useState({
    totalApplications: 0,
    pendingApplications: 0,
    approvedHospitals: 0,
    rejectedApplications: 0,
    thisMonthApplications: 0,
    averageApprovalTime: 0,
  });

  // Create New Case Modal State
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [loadingHospitals, setLoadingHospitals] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);

  const { userData: user } = useAuth();

  // Sidebar items with dynamic counts
  const sidebarItems = [
    { id: "dashboard", label: "Dashboard Overview", icon: LayoutDashboard, count: null },
    { id: "hospitals", label: "Manage Hospitals", icon: Building, count: null },
    { id: "applications-review", label: "Applications Review", icon: FileText, count: stats?.pendingApplications || 0 },
  ];

  const statusOptions = [
    { value: "", label: "All Status" },
    {
      value: "pending",
      label: "Pending Review",
      color: "bg-yellow-100 text-yellow-800",
    },
    {
      value: "approved",
      label: "Approved",
      color: "bg-green-100 text-green-800",
    },
    { value: "rejected", label: "Rejected", color: "bg-red-100 text-red-800" },
  ];

  const states = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];

  const loadDashboardData = async () => {
    try {
      // Load overall statistics
      console.log("user data:", user);
      const statsResult = await getLeadApplicationStats(user?.leadGeneratorId);
      if (statsResult.success) {
        setStats(statsResult.stats);
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    }
  };

  const loadHospitalsForCase = async () => {
    if (!user?.leadGeneratorId) return;
    
    setLoadingHospitals(true);
    try {
      const result = await getHospitalsByReferrer(user.leadGeneratorId);
      if (result.success) {
        // Filter only approved hospitals for case creation
        const approvedHospitals = result.hospitals.filter(hospital => hospital.status === 'approved');
        setHospitals(approvedHospitals);
      } else {
        console.error("Error loading hospitals:", result.error);
        setHospitals([]);
      }
    } catch (error) {
      console.error("Error loading hospitals:", error);
      setHospitals([]);
    } finally {
      setLoadingHospitals(false);
    }
  };

  const handleCreateCase = () => {
    setShowCreateCaseModal(true);
    loadHospitalsForCase();
  };

  const handleHospitalSelect = (hospital) => {
    setSelectedHospital(hospital);
  };

  const handleCreateCaseForHospital = () => {
    if (!selectedHospital) return;
    
    // Navigate to the new patient application for the selected hospital
    // We'll need to pass the hospital ID and ensure the lead generator info is included
    navigate(`/lead/createapplication?hospitalId=${selectedHospital.id}&hospitalName=${selectedHospital.hospitalName}&leadGeneratorId=${user?.leadGeneratorId}`);
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (activeSection === "hospitals") {
      loadHospitalApplications();
    }
  }, [activeSection, filters]);

  const loadHospitalApplications = async () => {
    setLoading(true);
    try {
      const result = await getHospitalApplications(filters.status);
      if (result.success) {
        let applications = result.applications;

        // Apply client-side filters
        if (filters.searchTerm) {
          applications = applications.filter(
            (app) =>
              app.hospitalName
                .toLowerCase()
                .includes(filters.searchTerm.toLowerCase()) ||
              app.applicationId
                .toLowerCase()
                .includes(filters.searchTerm.toLowerCase()) ||
              app.representative.name
                .toLowerCase()
                .includes(filters.searchTerm.toLowerCase())
          );
        }

        if (filters.state) {
          applications = applications.filter(
            (app) => app.state === filters.state
          );
        }

        if (filters.dateFrom) {
          applications = applications.filter(
            (app) =>
              new Date(app.submittedAt.toDate()) >= new Date(filters.dateFrom)
          );
        }

        if (filters.dateTo) {
          applications = applications.filter(
            (app) =>
              new Date(app.submittedAt.toDate()) <= new Date(filters.dateTo)
          );
        }

        setHospitalApplications(applications);
      }
    } catch (error) {
      console.error("Error loading hospital applications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const resetFilters = () => {
    setFilters({
      status: "",
      searchTerm: "",
      dateFrom: "",
      dateTo: "",
      state: "",
    });
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find((s) => s.value === status);
    return statusOption ? statusOption.color : "bg-gray-100 text-gray-800";
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleExportLeadApplications = async () => {
    try {
      // For lead dashboard, we export all applications created by this lead generator
      // This would need to fetch lead-specific applications
      const applications = []; // TODO: Fetch applications created by this lead generator
      const result = exportLeadGeneratorApplications(applications, 'lead_applications_lead_dashboard');
      if (result.success) {
        alert(result.message);
      } else {
        alert('Export failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error exporting lead applications:', error);
      alert('Failed to export lead applications');
    }
  };

  const handleExportDashboardStats = () => {
    try {
      const additionalData = {
        recentActivities: [
          { Activity: 'New Case Created', Details: 'Patient application for City Hospital submitted', Time: '1 hour ago' },
          { Activity: 'Hospital Approved', Details: 'Metro Hospital - Registration approved', Time: '3 hours ago' },
          { Activity: 'Case Reviewed', Details: 'Application completed doctor review', Time: '1 day ago' }
        ],
        monthlyTrends: [
          { Month: 'January', Cases: 25, Approvals: 22, Rejections: 3 },
          { Month: 'February', Cases: 31, Approvals: 28, Rejections: 3 },
          { Month: 'March', Cases: 28, Approvals: 25, Rejections: 3 }
        ]
      };
      
      const result = exportDashboardStats(stats, additionalData, 'lead_dashboard_statistics');
      if (result.success) {
        alert(result.message);
      } else {
        alert('Export failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error exporting dashboard statistics:', error);
      alert('Failed to export dashboard statistics');
    }
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Lead Dashboard</h1>
        <p className="text-gray-600">
          Overview of lead generator statistics and case management
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total Applications
              </p>
              <p className="text-2xl font-bold text-gray-800">
                {stats.totalApplications}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            <span className="text-green-600">+12%</span> from last month
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Pending Approval
              </p>
              <p className="text-2xl font-bold text-gray-800">
                {stats.pendingApplications}
              </p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Requires immediate attention
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Approved Hospitals
              </p>
              <p className="text-2xl font-bold text-gray-800">
                {stats.approvedHospitals}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            <span className="text-green-600">+5</span> this week
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Avg. Approval Time
              </p>
              <p className="text-2xl font-bold text-gray-800">
                {stats.averageApprovalTime} days
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            <span className="text-red-600">-0.5</span> days improvement
          </div>
        </div>
      </div>

  {/* Quick Actions */}
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Quick Actions
          </h3>
          <div className="space-y-3">
            <button
              onClick={handleCreateCase}
              className="w-full flex items-center justify-start space-x-3 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all"
            >
              <Plus className="h-5 w-5" />
              <span>Create New Case</span>
            </button>

            <button
              onClick={() => setActiveSection("hospitals")}
              className="w-full flex items-center justify-start space-x-3 px-4 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all"
            >
              <Building className="h-5 w-5" />
              <span>Manage Hospitals</span>
            </button>

            <button
              onClick={() => setActiveSection("applications-review")}
              className="w-full flex items-center justify-start space-x-3 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileText className="h-5 w-5" />
              <span>Review Patient Applications</span>
            </button>

            <button 
              onClick={handleExportDashboardStats}
              className="w-full flex items-center justify-start space-x-3 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="h-5 w-5" />
              <span>Generate Reports</span>
            </button>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <BarChart className="h-5 w-5 mr-2 text-teal-500" />
            Application Trends
          </h3>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
            <p className="text-gray-500">Chart will be implemented here</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <PieChart className="h-5 w-5 mr-2 text-teal-500" />
            Status Distribution
          </h3>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
            <p className="text-gray-500">Chart will be implemented here</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return renderDashboard();
      case "hospitals":
        return (
          <LeadGeneratorHospitals />
        );
      case "applications-review":
        return (
          <LeadApplicationsList />
        );
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-3">
            <div>
              <h1 className="text-xl font-bold text-gray-800">Lead Portal</h1>
              <p className="text-sm text-teal-600">Lead Portal</p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <NotificationBell />
            <div className="text-right">
              <p className="text-sm font-medium text-gray-800">{user?.entityName}</p>
              <p className="text-xs text-gray-600">{user?.email}</p>
            </div>
            <RoleSwitcher buttonClassName="border-gray-300 hover:border-teal-400 hover:text-teal-700" />
            {/* Removed decorative header icon */}
            <SignOutIconButton
              size="sm"
              showConfirmModal={true}
              confirmMode="modal"
              className="hover:bg-red-100"
            />
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white shadow-lg border-r border-gray-200 fixed lg:sticky top-16 lg:top-16 h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)] z-30 overflow-y-auto">
          <nav className="mt-8">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center justify-between px-6 py-3 text-left transition-colors duration-200 ${
                    isActive
                      ? "bg-teal-50 text-teal-600 border-r-2 border-teal-500"
                      : "text-gray-700 hover:bg-gray-50 hover:text-teal-600"
                  }`}
                >
                  <div className="flex items-center">
                    <Icon className="h-5 w-5 mr-3" />
                    {item.label}
                  </div>
                  {item.count !== null && item.count > 0 && (
                    <span className="bg-teal-100 text-teal-800 text-xs font-medium px-2 py-1 rounded-full">
                      {item.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:ml-0 ml-64">{renderContent()}</main>
      </div>

      {/* Create New Case Modal */}
      {showCreateCaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-800">Create New Case</h3>
              <button
                onClick={() => {
                  setShowCreateCaseModal(false);
                  setSelectedHospital(null);
                  setHospitals([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-800 mb-2">Select Hospital</h4>
                <p className="text-sm text-gray-600">
                  Choose a hospital from your network to create a new patient case
                </p>
              </div>

              {loadingHospitals ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-teal-500" />
                  <span className="ml-2 text-gray-600">Loading hospitals...</span>
                </div>
              ) : hospitals.length === 0 ? (
                <div className="text-center py-12">
                  <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Approved Hospitals Found
                  </h3>
                  <p className="text-gray-600 mb-4">
                    You don't have any approved hospitals in your network yet.
                  </p>
                  <button
                    onClick={() => setActiveSection("user-management")}
                    className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    <Building className="h-4 w-4 mr-2" />
                    View My Hospitals
                  </button>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {hospitals.map((hospital) => (
                    <div
                      key={hospital.id}
                      onClick={() => handleHospitalSelect(hospital)}
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${
                        selectedHospital?.id === hospital.id
                          ? "border-teal-500 bg-teal-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {hospital.hospitalName}
                          </h4>
                          <div className="text-sm text-gray-600 space-y-1">
                            <div className="flex items-center">
                              <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                              {hospital.city}, {hospital.state}
                            </div>
                            <div className="flex items-center">
                              <Phone className="h-4 w-4 mr-1 text-gray-400" />
                              {hospital.phone}
                            </div>
                            <div className="flex items-center">
                              <Mail className="h-4 w-4 mr-1 text-gray-400" />
                              {hospital.email}
                            </div>
                            <div className="flex items-center">
                              <Building className="h-4 w-4 mr-1 text-gray-400" />
                              {hospital.numberOfBeds} beds
                            </div>
                          </div>
                        </div>
                        <div className="ml-4">
                          {selectedHospital?.id === hospital.id && (
                            <CheckCircle className="h-6 w-6 text-teal-600" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {hospitals.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowCreateCaseModal(false);
                      setSelectedHospital(null);
                      setHospitals([]);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateCaseForHospital}
                    disabled={!selectedHospital}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Create Case for {selectedHospital?.hospitalName || "Selected Hospital"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDashboard;